import { templateRevision, refLanguage, refCountry } from "@tc/drizzle-v2"

import { eq, inArray } from "drizzle-orm"

import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { searchBuilderLog } from "../utils/logger"

const sqs_client = new SQSClient({})
const queue_url = process.env["QUEUE_URL"]

export type templateDoc = {
  code: string
  commodity: string | null
  commodities: string[] | null
  title: string
  destination_market: string | null
  displayed_description: string | null
  destination_countries: string[] | []
  exporting_country: string | null
  industry_access: boolean
  effective: number | null
  expiry: number | null
  certificate_type: string | null
  template_type: string | null
  id: string | null
  version_id: string | null
  major_version: number | null
  minor_version: number | null
  status: string | null
  submission_layout: string | null
  template_id: string
  languages: { code: string; name: string }[]
  countries: { code: string; name: string }[]
  pdf_layout: string | null
  xml_layout: string | null
  updated_at: number | null
}

export const getTemplateData = async (
  tx: DbTransaction,
  templateRevisionId: string,
): Promise<templateDoc | undefined> => {
  const [row] = await tx
    .select({
      code: templateRevision.code,
      comodity: templateRevision.commodity,
      commodities: templateRevision.commodities,
      title: templateRevision.title,
      destinationMarket: templateRevision.destinationMarket,
      destinationCountries: templateRevision.destinationCountries,
      exportingCountry: templateRevision.exportingCountry,
      industryAccess: templateRevision.industryAccess,
      effective: templateRevision.effective,
      expiry: templateRevision.expire,
      displayedDescription: templateRevision.displayedDescription,
      certificateType: templateRevision.certificateType,
      templateType: templateRevision.templateType,
      id: templateRevision.id,
      majorVersion: templateRevision.majorVersion,
      minorVersion: templateRevision.minorVersion,
      status: templateRevision.status,
      submissionLayout: templateRevision.submissionLayout,
      templateId: templateRevision.templateId,
      languageList: templateRevision.languageList,
      pdfLayout: templateRevision.pdfLayout,
      xmlLayout: templateRevision.xmlLayout,
      updatedAt: templateRevision.updatedAt,
    })
    .from(templateRevision)
    .where(eq(templateRevision.id, templateRevisionId))

  if (!row) {
    console.error(`Template with id ${templateRevisionId} not found`)
    return
  }

  const templateDoc: templateDoc = {
    id: row.id,
    version_id: row.id,
    code: row.code,
    commodity: row.comodity || null,
    commodities: row.comodity ? [row.comodity] : null,
    title: row.title,
    destination_market: row.destinationMarket || null,
    displayed_description: row.displayedDescription || null,
    destination_countries: Array.isArray(row.destinationCountries)
      ? row.destinationCountries
      : [],
    exporting_country: row.exportingCountry || null,
    industry_access: Boolean(row.industryAccess),
    effective: row.effective
      ? new Date(row.effective?.toString()).getTime()
      : null,
    expiry: row.expiry ? new Date(row.expiry?.toString()).getTime() : null,
    certificate_type: row.certificateType || null,
    template_type: row.templateType || null,
    major_version: row.majorVersion ? Number(row.majorVersion) : null,
    minor_version: row.minorVersion ? Number(row.minorVersion) : null,
    status: row.status || null,
    submission_layout: row.submissionLayout || null,
    template_id: row.templateId,
    languages: await resolveLanguages(
      tx,
      Array.isArray(row.languageList) ? row.languageList : [],
    ),
    countries: await resolveCountries(
      tx,
      Array.isArray(row.destinationCountries) ? row.destinationCountries : [],
    ),
    pdf_layout: row.pdfLayout || null,
    xml_layout: row.xmlLayout || null,
    updated_at: row.updatedAt
      ? new Date(row.updatedAt.toString()).getTime()
      : Date.now(),
  }

  searchBuilderLog.debug({
    document: templateDoc,
  })

  return templateDoc
}

export const saveTemplateToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getTemplateData(tx, id)

  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${id}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}

const resolveLanguages = async (
  tx: DbTransaction,
  languageCodes: string[] | null | undefined,
): Promise<{ code: string; name: string }[]> => {
  if (!languageCodes || languageCodes.length === 0) return []

  // language_list stores mixed data: some are codes ("en"), some are names ("English")
  // Query by both language_code and language_name to handle both cases
  const byCode = await tx
    .select({
      code: refLanguage.itemCode,
      name: refLanguage.itemName,
    })
    .from(refLanguage)
    .where(inArray(refLanguage.itemCode, languageCodes))

  const byName = await tx
    .select({
      code: refLanguage.itemCode,
      name: refLanguage.itemName,
    })
    .from(refLanguage)
    .where(inArray(refLanguage.itemName, languageCodes))

  // Merge results, deduplicate by code
  const seen = new Set<string>()
  const result: { code: string; name: string }[] = []

  for (const row of [...byCode, ...byName]) {
    if (!seen.has(row.code)) {
      seen.add(row.code)
      result.push({ code: row.code, name: row.name ?? row.code })
    }
  }

  return result
}

const resolveCountries = async (
  tx: DbTransaction,
  countryCodes: string[] | null | undefined,
): Promise<{ code: string; name: string }[]> => {
  if (!countryCodes || countryCodes.length === 0) return []

  const rows = await tx
    .select({
      code: refCountry.itemCode,
      name: refCountry.itemName,
    })
    .from(refCountry)
    .where(inArray(refCountry.itemCode, countryCodes))

  return rows
    .filter(
      (row): row is { code: string; name: string | null } => row.code !== null,
    )
    .map((row) => ({
      code: row.code,
      name: row.name ?? row.code,
    }))
}
